import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate, convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import clearSearch from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ClearSearch";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ToggleFiltersOpen";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import MyAccountThemeWrapper from "@tSurya/Utils/MyAccountThemeWrapper";
import SuryaPageThemeWrapper from "@tSurya/Utils/SuryaPageThemeWrapper";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isFilterOpen: state.pages.savedOrderList.isFilterOpen,
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
    language: getSession(state).language,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
    toggleFiltersOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListFilterStyles {
    container?: GridContainerProps;
    container2?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    datesGridItem?: GridItemProps;
    datesContainer?: GridContainerProps;
    dateRangeFieldset?: InjectableCss;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalContainer?: GridContainerProps;
    orderTotalFieldset?: InjectableCss;
    orderTotalOperatorGridItem?: GridItemProps;
    orderTotalOperatorSelect?: SelectPresentationProps;
    orderTotalAmountGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    toggleFilterGridItem?: GridItemProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonPlusIcon?: ButtonIconPresentationProps;
    toggleFilterButtonMinusIcon?: ButtonIconPresentationProps;
    toggleFilter?: GridItemProps;
    filterText?: GridItemProps;
}

export const savedOrderListFilterStyles: SavedOrderListFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    container2:{
        css: css`
            background: rgb(248, 248, 248);
            padding: 10px 20px;
            border-radius: 3px;
            display: flex;
            justify-content: space-between;
            margin-bottom: 25px;
            border: 1px solid rgb(224, 224, 224) !important;
            cursor: pointer;
            white-space: nowrap;
        `,
    },
    toggleFilterButton: {
        shadow: false,
        css: css`
            width: 40px;
            padding: 0;
            background: none !important;
            border: none !important;
        `,
    },
    toggleFilterButtonPlusIcon: {
        size: 24,
        css: css`
            color: ${getColor("primary")} !important;
            position: relative !important;
            left: 15px;
            top: -14px !important;
            svg {
                width: 17px;
                height: 17px;
            }
        `,
    },
    toggleFilterButtonMinusIcon: {
        size: 24,
        css: css`
            color: ${getColor("primary")} !important;
            svg {
                width: 17px;
                height: 17px;
            }
        `,
    },
    toggleFilterGridItem: {
        width: 12,
        style: { marginTop: "8px", justifyContent: "flex-end", marginBottom: "15px", paddingBottom: "5px" },
    },
    headingGridItem: {
        width: 12,
        style: { marginTop: "-53px" },
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    datesGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: [12, 12, 6, 4, 4],
    },
    datesContainer: { gap: 10 },
    dateRangeFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    // fromGridItem: { width: 6 },
    // toGridItem: { width: 6 },
    appliedFiltersContainer: { css: horizontalStyles },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    orderTotalGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: [12, 12, 6, 4, 4],
    },
    orderTotalContainer: { gap: 10 },
    orderTotalFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    orderTotalOperatorGridItem: { width: 6 },
    orderTotalAmountGridItem: { width: 6 },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 32px;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    filterText: {
        css: css`
            font-size: large;
            font-weight: bolder;
        `,
    },
    toggleFilter: {
        css: css`
            justify-Content: flex-end
        `,
    },
};

const StyledDateRangeFieldSet = getStyledWrapper("fieldset");

/**
 * MGBW bakes a dark hover (background #333, white text) into theme.button.secondary
 * via a global `.ButtonWrapper:hover` rule injected in Start.tsx. That global rule
 * wins over per-button `css`/`hoverStyle` props, so we neutralize the hover here by
 * pinning it back to the resting state from a parent wrapper, targeting the button by
 * its data-test-selector with !important. This matches Surya, where the Clear Search
 * button has no hover effect. MGBW only; `display: contents` keeps layout unchanged.
 */
const clearSearchHoverFixCss = css`
    display: contents;

    button[data-test-selector="savedOrderList_clearFiltersButton"]:not(:disabled) {
        color: #545454 !important;
    }

    button[data-test-selector="savedOrderList_clearFiltersButton"]:not(:disabled) * {
        color: #545454 !important;
    }

    button[data-test-selector="savedOrderList_clearFiltersButton"]:not(:disabled):hover {
        background: #ffffff !important;
        border-color: #333333 !important;
        color: #545454 !important;
        opacity: 1 !important;
        transform: none !important;
    }

    button[data-test-selector="savedOrderList_clearFiltersButton"]:not(:disabled):hover * {
        color: #545454 !important;
    }
`;

export const styles = savedOrderListFilterStyles;
let updateTimeoutId: NodeJS.Timeout;
const SavedOrderListFilter = ({
    isFilterOpen,
    getCartsApiParameter,
    updateSearchFields,
    clearSearch,
    language,
    toggleFiltersOpen,
}: Props) => {
    const ThemeWrapper = isMgbwWebsite() ? SuryaPageThemeWrapper : MyAccountThemeWrapper;
    const ClearSearchHoverFix = isMgbwWebsite()
        ? ({ children }: { children: React.ReactNode }) => (
              <StyledWrapper css={clearSearchHoverFixCss}>{children}</StyledWrapper>
          )
        : React.Fragment;
    const [orderSubtotal, setOrderSubtotal] = React.useState("");
    const [orderSubtotalOperator, setOrderSubtotalOperator] = React.useState("");
    const [isPulsing, setIsPulsing] = React.useState(true);

    useEffect(() => {
        if (!isFilterOpen) {
            toggleFiltersOpen();
        }
        if (
            !orderSubtotal &&
            !orderSubtotalOperator &&
            getCartsApiParameter.orderSubtotal &&
            getCartsApiParameter.orderSubtotalOperator
        ) {
            setOrderSubtotal(getCartsApiParameter.orderSubtotal);
            setOrderSubtotalOperator(getCartsApiParameter.orderSubtotalOperator);
        }
    }, []);

    const handleClick = () => {
        setIsPulsing(prevState => !prevState);
        toggleFiltersOpen();
    };

    const orderSubtotalChangeHandler = (totalValue: string, totalOperator: string) => {
        if ((totalValue === "" && totalOperator !== "") || (totalValue !== "" && totalOperator === "")) {
            return;
        }

        updateSearchFields({
            orderSubtotal: totalValue,
            orderSubtotalOperator: totalOperator,
        });
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const orderSubtotalValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const total = event.target.value;
        setOrderSubtotal(total);

        clearTimeout(updateTimeoutId);

        updateTimeoutId = setTimeout(() => {
            orderSubtotalChangeHandler(total, orderSubtotalOperator);
        }, 350);
    };

    const orderSubtotalOperatorChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const operator = event.target.value;
        setOrderSubtotalOperator(operator);

        orderSubtotalChangeHandler(orderSubtotal, operator);
    };

    const resetOrderSubtotal = () => {
        setOrderSubtotal("");
        updateSearchFields({ orderSubtotalOperator: "", orderSubtotal: "" });
    };

    const clearFiltersClickHandler = () => {
        clearSearch();
        setOrderSubtotal("");
        setOrderSubtotalOperator("");
    };

    if (!isFilterOpen) {
        return (
            <ThemeWrapper>
            <StyledWrapper {...styles.container2} onClick={handleClick}>
                <StyledWrapper {...styles.filterText}>Search Saved Orders</StyledWrapper>
                <StyledWrapper {...styles.toggleFilter}>
                    <ButtonIcon src={Plus} size={14} />
                </StyledWrapper>
            </StyledWrapper >
                <StyledWrapper {...styles.appliedFiltersContainer}>
                    {getCartsApiParameter.fromDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fromDateChangeHandler({});
                            }}
                        >
                            {`${translate("From")}: ${getLocalizedDateTime({
                                dateTime: convertDateOnlyStringToDate(getCartsApiParameter.fromDate)!,
                                language,
                            })}`}{" "}
                        </Tag>
                    )}
                    {getCartsApiParameter.toDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                toDateChangeHandler({});
                            }}
                        >
                            {`${translate("To")}: ${getLocalizedDateTime({
                                dateTime: convertDateOnlyStringToDate(getCartsApiParameter.toDate)!,
                                language,
                            })}`}{" "}
                        </Tag>
                    )}
                    {getCartsApiParameter.orderSubtotalOperator && getCartsApiParameter.orderSubtotal && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                resetOrderSubtotal();
                            }}
                        >
                            {`${translate("Order Total")}: ${getCartsApiParameter.orderSubtotalOperator} ${
                                getCartsApiParameter.orderSubtotal
                            }`}
                        </Tag>
                    )}
                </StyledWrapper>
            </ThemeWrapper>
        );
    }

    const fromDate = getCartsApiParameter.fromDate ? new Date(getCartsApiParameter.fromDate) : undefined;
    const toDate = getCartsApiParameter.toDate ? new Date(getCartsApiParameter.toDate) : undefined;

    return (
        <ThemeWrapper>
            <>
             <StyledWrapper {...styles.container2} onClick={handleClick}>
                    <StyledWrapper {...styles.filterText}>Search Saved Orders</StyledWrapper>
                    <StyledWrapper {...styles.toggleFilter}>
                        <ButtonIcon src={Minus} size={14} />
                    </StyledWrapper>
                </StyledWrapper >
            <ClearSearchHoverFix>
            <GridContainer {...styles.container}>
            {/* <GridItem {...styles.orderTotalGridItem}>
                <StyledOrderTotalFieldSet {...styles.orderTotalFieldset}>
                    <VisuallyHidden as="label">{translate("Order Subtotal")}</VisuallyHidden>
                    <GridContainer {...styles.orderTotalContainer}>
                        <GridItem {...styles.orderTotalOperatorGridItem}>
                            <Select
                                {...styles.orderTotalOperatorSelect}
                                label={translate("Order Subtotal")}
                                value={orderSubtotalOperator}
                                data-test-selector="savedOrderList_comparisonMethod"
                                onChange={orderSubtotalOperatorChangeHandler}
                            >
                                <option value="">{translate("Select")}</option>
                                <option key="Greater Than" value="Greater Than">
                                    {translate("Greater Than")}
                                </option>
                                <option key="Less Than" value="Less Than">
                                    {translate("Less Than")}
                                </option>
                                <option key="Equal To" value="Equal To">
                                    {translate("Equal To")}
                                </option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.orderTotalAmountGridItem}>
                            <TextField
                                {...styles.orderTotalAmountTextField}
                                value={orderSubtotal}
                                type="number"
                                min={0}
                                max={9999999999}
                                data-test-selector="savedOrderList_comparisonValue"
                                onChange={orderSubtotalValueChangeHandler}
                                
                            />
                        </GridItem>
                    </GridContainer>
                </StyledOrderTotalFieldSet>
            </GridItem> */}
            <GridItem {...styles.datesGridItem}>
                <StyledDateRangeFieldSet {...styles.dateRangeFieldset}>
                    <VisuallyHidden as="label">{translate("Date Range")}</VisuallyHidden>
                    <GridContainer {...styles.datesContainer}>
                        <GridItem data-test-selector="savedOrderList_fromDateFilter" {...styles.fromGridItem}>
                            <DatePicker
                                {...styles.fromDate}
                                label={translate("Date Range")}
                                selectedDay={fromDate}
                                onDayChange={fromDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: fromDate ? undefined : null,
                                    maxDate: toDate,
                                    ...styles.fromDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                        <GridItem data-test-selector="savedOrderList_toDateFilter" {...styles.toGridItem}>
                            <DatePicker
                                {...styles.toDate}
                                label={translate(" ")}
                                selectedDay={toDate}
                                onDayChange={toDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: toDate ? undefined : null,
                                    minDate: fromDate,
                                    ...styles.toDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                    </GridContainer>
                </StyledDateRangeFieldSet>
            </GridItem>
            <GridItem {...styles.buttonsItem}>
                <Button
                    {...styles.clearFiltersButton}
                    data-test-selector="savedOrderList_clearFiltersButton"
                    onClick={clearFiltersClickHandler}
                >
                    {translate("Clear Search")}
                </Button>
            </GridItem>
        </GridContainer>
            </ClearSearchHoverFix>
        </>
        </ThemeWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrderListFilter),
    definition: {
        group: "Saved Order List",
        displayName: "Search Results Filter",
        allowedContexts: ["SavedOrderListPage"],
        isSystem: true,
    },
};

export default widgetModule;
